import { useMemo, useState } from 'react'
import { csvCodec, useUrlState } from './useUrlState'

// Backs a ClassroomFilterDropdown: `selected*` is the dropdown's draft,
// `applied*` (URL-backed, under the given param names) is what actually scopes
// the owning screen's list. `filterOptions` (its `filterYears`/`classesOfYear`)
// comes from that screen's own data - this hook only owns the filter's
// selection state, not the option list itself. Each screen that filters a
// different dataset needs its own independent instance. `pseudoYears` (only
// Teacher Management uses this) names "year" pills that aren't real years -
// toggling one must not reset the class selection the way toggling a real year
// does, since they don't interact with the classes row at all (see
// teacherRoleBuckets.js).
export function useYearClassFilter(yearsParam, classesParam, filterOptions, pseudoYears = []) {
    const [appliedYearFilter, setAppliedYearFilter] = useUrlState(yearsParam, ['all'], { ...csvCodec(), isDefault: (v) => v.includes('all') })
    const [appliedClassFilter, setAppliedClassFilter] = useUrlState(classesParam, ['all'], { ...csvCodec(), isDefault: (v) => v.includes('all') })
    const [selectedYearFilter, setSelectedYearFilter] = useState(appliedYearFilter)
    const [selectedClassFilter, setSelectedClassFilter] = useState(appliedClassFilter)

    const selectedYearList = useMemo(
        () =>
            selectedYearFilter.includes('all')
                ? filterOptions.classesOfYear.all
                : Array.from(new Set(selectedYearFilter.flatMap((y) => filterOptions.classesOfYear[y] || []))),
        [selectedYearFilter, filterOptions]
    )

    const selectAllYears = () => {
        setSelectedYearFilter(['all'])
        setSelectedClassFilter(['all'])
    }

    const selectYearFilter = (year) => {
        if (year === 'all') {
            selectAllYears()
            return
        }
        const withoutAll = selectedYearFilter.filter((y) => y !== 'all')
        const next = withoutAll.includes(year) ? withoutAll.filter((y) => y !== year) : [...withoutAll, year]
        setSelectedYearFilter(next.length ? next : ['all'])
        if (!pseudoYears.includes(year)) setSelectedClassFilter(['all'])
    }

    const selectAllClasses = () => setSelectedClassFilter(['all'])

    const selectClass = (cls) => {
        const withoutAll = selectedClassFilter.filter((c) => c !== 'all')
        const next = withoutAll.includes(cls) ? withoutAll.filter((c) => c !== cls) : [...withoutAll, cls]
        setSelectedClassFilter(next.length ? next : ['all'])
    }

    const applyFilter = () => {
        setAppliedYearFilter(selectedYearFilter)
        setAppliedClassFilter(selectedClassFilter)
    }

    const resetFilter = () => {
        setSelectedYearFilter(['all'])
        setSelectedClassFilter(['all'])
        setAppliedYearFilter(['all'])
        setAppliedClassFilter(['all'])
    }

    return { appliedYearFilter, appliedClassFilter, selectedYearFilter, selectYearFilter, selectedClassFilter, selectedYearList, selectAllClasses, selectClass, applyFilter, resetFilter }
}
